/**
 * bye-management.ts - Bye Request Management
 *
 * Handles bye requests for Swiss tournaments, allowing tournament organizers
 * to assign byes to players for specific rounds with configurable point awards.
 */

import type { Player } from '../../../types/api';
import { API_BASE_URL } from '../../utils/constants';
import { t } from '../../utils/i18n-helper';

interface ByeRequest {
  points: number; // 0.0, 0.5, or 1.0
}

/**
 * Open the bye request management modal for a player
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export async function openByeModal(tournamentId: string, playerId: string): Promise<void> {
  console.log('[ByeManagement] openByeModal called with:', { tournamentId, playerId });
  try {
    // Fetch tournament data
    const response = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      alert(t('error.loadTournament', 'Error loading tournament'));
      return;
    }

    const tournament = await response.json();
    const player = tournament.players.find((p: Player) => p.id === playerId);

    if (!player) {
      alert(t('player.notFound', 'Player not found'));
      return;
    }

    console.log('Opening bye modal for player:', player);
    console.log('Player bye requests:', player.byeRequests);

    console.log('[ByeManagement] Tournament format:', tournament.format, 'numberOfRounds:', tournament.numberOfRounds);

    if (tournament.format !== 'swiss' || !tournament.numberOfRounds) {
      alert(t('player.byeOnlySwiss', 'Bye requests are only available for Swiss tournaments'));
      return;
    }

    console.log('[ByeManagement] Creating modal...');

    // Create modal
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.id = `bye-modal-${tournamentId}-${playerId}`;
    // Force visibility with inline styles for debugging
    modal.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 99999; opacity: 1;';

    const byeRequests = player.byeRequests || {};
    const numberOfRounds = tournament.numberOfRounds;

    let modalContent = `
      <div class="modal-content bye-modal" style="background: white; border-radius: 12px; max-width: 500px; width: 90%; max-height: 80vh; overflow-y: auto; box-shadow: 0 8px 32px rgba(0,0,0,0.2);">
        <div class="modal-header" style="display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e5e7eb;">
          <h3 style="margin: 0; font-size: 1.25rem;">${t('player.manageByes', 'Manage Bye Requests')}</h3>
          <button class="modal-close" onclick="closeByeModal('${tournamentId}', '${playerId}')" style="background: none; border: none; font-size: 24px; cursor: pointer; color: #6b7280;">&times;</button>
        </div>
        <div class="modal-body" style="padding: 20px;">
          <p class="player-name-display" style="font-weight: 600; margin-bottom: 8px;">
            ${player.firstName} ${player.lastName}
            ${player.rating ? ` (${player.rating})` : ''}
          </p>
          <p class="bye-instructions" style="color: #6b7280; font-size: 0.9rem; margin-bottom: 16px;">
            ${t('player.byeInstructions', 'Set bye requests for specific rounds. Choose the points awarded (0, 0.5, or 1).')}
          </p>
          <div class="bye-rounds-container" style="display: flex; flex-direction: column; gap: 12px;">
    `;

    for (let round = 1; round <= numberOfRounds; round++) {
      const existingBye = byeRequests[round];
      const hasMatches = tournament.matches.some((m: any) =>
        m.round === round && (m.player1?.id === playerId || m.player2?.id === playerId)
      );

      modalContent += `
        <div class="bye-round-row" style="display: flex; align-items: center; gap: 16px; padding: 8px 12px; background: #f9fafb; border-radius: 8px;">
          <label class="bye-round-label" style="display: flex; align-items: center; gap: 8px; cursor: pointer; min-width: 120px;">
            <input
              type="checkbox"
              id="bye-round-${round}"
              data-round="${round}"
              ${existingBye ? 'checked' : ''}
              ${hasMatches ? 'disabled' : ''}
              onchange="toggleByeRound(${round})"
              style="width: 18px; height: 18px; cursor: pointer;"
            >
            <span>${t('player.round', 'Round')} ${round}</span>
          </label>
          <div class="bye-points-selector" id="bye-points-${round}" style="display: ${existingBye ? 'flex' : 'none'}; gap: 12px;">
            <label style="display: flex; align-items: center; gap: 4px; cursor: pointer;">
              <input
                type="radio"
                name="points-round-${round}"
                value="0"
                ${existingBye && existingBye.points === 0 ? 'checked' : ''}
                ${hasMatches ? 'disabled' : ''}
              >
              <span>0 ${t('player.points', 'pts')}</span>
            </label>
            <label style="display: flex; align-items: center; gap: 4px; cursor: pointer;">
              <input
                type="radio"
                name="points-round-${round}"
                value="0.5"
                ${existingBye && existingBye.points === 0.5 ? 'checked' : ''}
                ${existingBye && existingBye.points !== 0 && existingBye.points !== 1 ? 'checked' : ''}
                ${hasMatches ? 'disabled' : ''}
              >
              <span>0.5 ${t('player.points', 'pts')}</span>
            </label>
            <label style="display: flex; align-items: center; gap: 4px; cursor: pointer;">
              <input
                type="radio"
                name="points-round-${round}"
                value="1"
                ${existingBye && existingBye.points === 1 ? 'checked' : ''}
                ${hasMatches ? 'disabled' : ''}
              >
              <span>1 ${t('player.points', 'pt')}</span>
            </label>
          </div>
          ${hasMatches ? `<span style="color: #dc2626; font-size: 0.85rem; font-style: italic;">${t('player.roundHasMatches', 'Round already has matches')}</span>` : ''}
        </div>
      `;
    }

    modalContent += `
          </div>
        </div>
        <div class="modal-footer" style="display: flex; justify-content: flex-end; gap: 12px; padding: 16px 20px; border-top: 1px solid #e5e7eb;">
          <button onclick="closeByeModal('${tournamentId}', '${playerId}')" style="padding: 8px 16px; border: 1px solid #d1d5db; background: white; border-radius: 6px; cursor: pointer; font-size: 14px;">
            ${t('common.cancel', 'Cancel')}
          </button>
          <button onclick="saveByeRequests('${tournamentId}', '${playerId}')" style="padding: 8px 16px; border: none; background: #667eea; color: white; border-radius: 6px; cursor: pointer; font-size: 14px;">
            ${t('common.save', 'Save')}
          </button>
        </div>
      </div>
    `;

    modal.innerHTML = modalContent;
    document.body.appendChild(modal);
    console.log('[ByeManagement] Modal appended to body');

    // Show modal
    setTimeout(() => {
      modal.classList.add('show');
      console.log('[ByeManagement] Modal show class added');
    }, 10);
  } catch (error) {
    console.error('Error opening bye modal:', error);
    alert(t('error.generic', 'An error occurred'));
  }
}

/**
 * Close the bye request modal
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export function closeByeModal(tournamentId: string, playerId: string): void {
  const modal = document.getElementById(`bye-modal-${tournamentId}-${playerId}`);
  if (modal) {
    modal.classList.remove('show');
    setTimeout(() => {
      modal.remove();
    }, 300);
  }
}

/**
 * Toggle bye round checkbox
 * @param round - The round number
 */
export function toggleByeRound(round: number): void {
  const checkbox = document.getElementById(`bye-round-${round}`) as HTMLInputElement;
  const pointsSelector = document.getElementById(`bye-points-${round}`) as HTMLElement;

  if (!checkbox || !pointsSelector) return;

  if (checkbox.checked) {
    pointsSelector.style.display = 'flex';
    // Default to 0.5 points if not already set
    const radios = pointsSelector.querySelectorAll('input[type="radio"]');
    const hasChecked = Array.from(radios).some((r: Element) => (r as HTMLInputElement).checked);
    if (!hasChecked) {
      const halfPointRadio = pointsSelector.querySelector('input[value="0.5"]') as HTMLInputElement;
      if (halfPointRadio) halfPointRadio.checked = true;
    }
  } else {
    pointsSelector.style.display = 'none';
  }
}

/**
 * Save bye requests for a player
 * @param tournamentId - The tournament ID
 * @param playerId - The player ID
 */
export async function saveByeRequests(tournamentId: string, playerId: string): Promise<void> {
  try {
    // Fetch current player data
    const response = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      alert(t('error.loadTournament', 'Error loading tournament'));
      return;
    }

    const tournament = await response.json();
    const player = tournament.players.find((p: Player) => p.id === playerId);

    if (!player) {
      alert(t('player.notFound', 'Player not found'));
      return;
    }

    // Collect bye requests from the form
    const byeRequests: Record<number, ByeRequest> = {};
    const numberOfRounds = tournament.numberOfRounds;

    console.log('Collecting bye requests for', numberOfRounds, 'rounds');

    for (let round = 1; round <= numberOfRounds; round++) {
      const checkbox = document.getElementById(`bye-round-${round}`) as HTMLInputElement;

      if (checkbox && checkbox.checked && !checkbox.disabled) {
        const selectedRadio = document.querySelector(
          `input[name="points-round-${round}"]:checked`
        ) as HTMLInputElement;

        if (selectedRadio) {
          const points = parseFloat(selectedRadio.value);
          byeRequests[round] = { points };
          console.log(`Round ${round}: bye request with ${points} points`);
        } else {
          console.warn(`Round ${round}: checkbox checked but no radio selected`);
        }
      }
    }

    console.log('Collected bye requests:', byeRequests);

    // Update player with bye requests
    const requestBody = {
      firstName: player.firstName,
      lastName: player.lastName,
      rating: player.rating,
      title: player.title,
      club: player.club,
      federation: player.federation,
      fideId: player.fideId,
      federationCode: player.federationCode,
      byeRequests: Object.keys(byeRequests).length > 0 ? byeRequests : undefined
    };

    console.log('Sending update request:', requestBody);

    const updateResponse = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });

    if (!updateResponse.ok) {
      const errorData = await updateResponse.json().catch(() => ({}));
      console.error('Failed to save bye requests:', errorData);
      alert(t('error.saveFailed', 'Error saving bye requests') + ': ' + (errorData.error || updateResponse.statusText));
      return;
    }

    const responseData = await updateResponse.json();
    console.log('Update response:', responseData);

    // Close modal and reload tournaments
    closeByeModal(tournamentId, playerId);

    if (window.loadSingleTournament) {
      await window.loadSingleTournament(tournamentId);
    }
  } catch (error) {
    console.error('Error saving bye requests:', error);
    alert(t('error.generic', 'An error occurred'));
  }
}

// Export to window for onclick handlers
window.openByeModal = openByeModal;
window.closeByeModal = closeByeModal;
window.toggleByeRound = toggleByeRound;
window.saveByeRequests = saveByeRequests;
